import { Box, Flex, Stack, Text } from '@chakra-ui/react'
import React from 'react'
import Link from 'next/link'
import { isEmpty, flatMap } from 'lodash-es'
import { LiveListItemFragment } from 'web-queries/src/types/generated'
import LivePoster from './LivePoster'
import { dateToString, getDateDistance } from '../utils/date'
import { TiMediaRecord } from 'react-icons/ti'
import LiveBadge from './LiveBadge'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import MusicianTag from './MusicianTag'
import { generateStageLink } from '../utils/link'

interface Props {
  live: LiveListItemFragment
}

export default function FestivalItem({ live }: Props) {
  const { t } = useTranslation('live')
  const { locale } = useRouter()
  const url = `/live/${live.id}`
  const ddayText = getDateDistance(live.startDate)

  const startDate = dateToString(new Date(live.startDate), locale)
  const endDate = dateToString(new Date(live.endDate), locale)

  const lineup = live.festival
    ? flatMap(
        live.festival.festivalItems.map(festivalItem =>
          festivalItem.festivalItemTimeTables.map(timetable => ({
            id: timetable.id,
            name: timetable.musician?.name ?? '',
            useSlug: false,
            slug: '',
          }))
        )
      )
    : live.musicians.map(musician => ({
        id: musician.id,
        name: musician.name,
        useSlug: musician.useSlug,
        slug: musician.slug,
      }))
  return (
    <Box position="relative" paddingBottom={10}>
      <Link href={url} passHref prefetch={false}>
        <Box as="a" display="block">
          <LivePoster livePoster={live} />
          <Box mt={4} position="relative">
            <Text fontSize="1.2rem" fontWeight="bold" marginBottom="4px">
              {live.isCanceled ? `[${t('canceled')}] ${live.title}` : live.title}
            </Text>
            {live.stage && (
              <Link href={generateStageLink(live.stage)} prefetch={false}>
                <Text>장소: {live.stage?.name}</Text>
              </Link>
            )}
            <Text>
              {t('festival-date', {
                startDate,
                endDate,
              })}
            </Text>
          </Box>
        </Box>
      </Link>
      <Stack direction="row" wrap="wrap" marginTop="8px">
        {lineup
          .filter(lineup => lineup.name)
          .map(musician => (
            <MusicianTag key={`musician-${musician.id}`} {...musician} />
          ))}
      </Stack>
      {live.liveStreaming && (
        <LiveBadge top="12px" left="12px" bgColor="red.700">
          <Flex>
            <TiMediaRecord size={20} /> Streaming
          </Flex>
        </LiveBadge>
      )}
      {!isEmpty(ddayText) && (
        <LiveBadge top="12px" right="12px">
          {ddayText}
        </LiveBadge>
      )}
    </Box>
  )
}
